import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface UserProfile {
  id: string;
  username: string | null;
  display_name: string | null;
}

export function AuthCheck({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onboardingStatus = useOnboardingStatus();
  const [profileLoading, setProfileLoading] = useState(true);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);

  useEffect(() => {
    let mounted = true;

    const checkUserProfile = async () => {
      if (!user) {
        setProfileLoading(false);
        return;
      }

      try {
        // Check if user has a profile in the new users table
        const { data: profile, error } = await supabase
          .from('users')
          .select('id, username, display_name')
          .eq('id', user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user profile:', error);
          setProfileLoading(false);
          return;
        }

        if (mounted) {
          setUserProfile(profile);
        }
      } catch (error) {
        console.error('Unexpected error checking profile:', error);
      } finally {
        if (mounted) {
          setProfileLoading(false);
        }
      }
    };

    if (!authLoading) {
      checkUserProfile();
    }

    return () => {
      mounted = false;
    };
  }, [user, authLoading, navigate, location.pathname]);

  // Handle onboarding redirect logic (only when Safe Mode is off)
  useEffect(() => {
    if (!user || authLoading || profileLoading || onboardingStatus.loading) return;

    // Don't redirect if already on auth page
    if (location.pathname === '/auth') return;

    // Skip onboarding redirects if Safe Mode is on
    const safeMode = import.meta.env.VITE_SAFE_MODE === 'true';
    if (safeMode) return;

    // If there's an error checking onboarding status, don't force redirects
    if (onboardingStatus.error) return;

    // If onboarding not complete, redirect based on next step
    if (onboardingStatus.nextStep !== 'done') {
      const currentPath = location.pathname;
      
      // Don't redirect if already on the correct onboarding step
      if (
        (onboardingStatus.nextStep === 'profile' && currentPath === '/onboarding/profile') ||
        (onboardingStatus.nextStep === 'import' && currentPath === '/onboarding/import') ||
        (onboardingStatus.nextStep === 'genres' && currentPath === '/onboarding/genres') ||
        currentPath.startsWith('/onboarding')
      ) {
        return;
      }

      // Redirect to appropriate onboarding step
      if (onboardingStatus.nextStep === 'profile') {
        navigate('/onboarding/profile', { replace: true });
      } else if (onboardingStatus.nextStep === 'import') {
        navigate('/onboarding/import', { replace: true });
      } else if (onboardingStatus.nextStep === 'genres') {
        navigate('/onboarding/genres', { replace: true });
      }
      return;
    }

    // Check if user is in "retake mode" (explicitly revisiting onboarding)
    const isRetakeMode = sessionStorage.getItem('retakeOnboarding') === '1' || 
                         new URLSearchParams(location.search).get('retake') === '1';
    
    // If onboarding complete and on onboarding pages, redirect to home (unless retaking)
    // Allow bookshelf scan page even after onboarding is complete
    const isBookshelfScan = location.pathname.startsWith('/onboarding/bookshelf-scan');
    if (onboardingStatus.nextStep === 'done' && location.pathname.startsWith('/onboarding') && !isRetakeMode && !isBookshelfScan) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, profileLoading, onboardingStatus, navigate, location.pathname]);

  // Cleanup retake mode flag when leaving onboarding
  useEffect(() => {
    if (!location.pathname.startsWith('/onboarding')) {
      sessionStorage.removeItem('retakeOnboarding');
    }
  }, [location.pathname]);

  // Show loading while checking auth, profile, or onboarding status
  if (authLoading || profileLoading || onboardingStatus.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  // Allow public routes even when not authenticated
  const publicRoutes = ['/auth', '/u/', '/healthz'];
  const isPublicRoute = publicRoutes.some(route => location.pathname.startsWith(route));

  // If not authenticated and not on a public route, redirect to auth
  if (!user && !isPublicRoute) {
    navigate('/auth', { replace: true });
    return null;
  }

  return <>{children}</>;
}